import { CircularProgress, Modal, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios, { AxiosError, AxiosResponse } from "axios";
import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Divider,
  LoadingHistoryContainer,
  LoadingHistoryMessage,
  MessagesContainer, ModalContainer,
  ModalHeaderContainer, RootContainer, SessionTerminatedMessage, SessionTerminatedMessageContainer,
  TitleContainer,
  WelcomeMessage, WelcomeMessageContainer } from "./AISupportChat.styles";
import { ChatAgentMessage, ChatAgentRequestPayload, ChatAgentResponse } from "./AISupportChat.types";
import AISupportChatErrorOverlay from "./components/AISupportChatErrorOverlay/AISupportChatErrorOverlay.component";
import AISupportChatMessageBox from "./components/AISupportChatMessageBox/AISupportChatMessageBox.component";
import AISupportChatMessageInput from "./components/AISupportChatMessageInput/AISupportChatMessageInput";
import { useAISupportChat } from "../../context/AISupportChatContext";
import ButtonCst from "../ButtonCst";
import { Icon } from "../Icon";

const apiTimeout = 30000; //30 seconds

const AISupportChat = () => {

  const { t } = useTranslation();
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatAgentMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isRetrievingHistory, setIsRetrievingHistory] = useState(false);
  const [blockUserInput, setBlockUserInput] = useState(false);
  const [apiError, setApiError] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isOpen, closeChat: onClose, sessionId } = useAISupportChat();

  const apiInstance = axios.create({
    timeout: apiTimeout,
    headers: {
      Authorization: `Bearer ${sessionId}`,
      "x-ada-session-token": sessionId
    }
  });

  useEffect(() => {
    if (
      messagesContainerRef.current &&
      messages &&
      messages.filter((message) => !message.from_history).length > 0
    ) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === "user") {
        setMessages([
          ...messages,
          {
            sender: "assistant",
            end_session: false,
            fist_message: false,
            response: ""
          }
        ]);
      } else {
        retrieveMessageFromAgent();
      }

      scrollToLastMessage();
    }
  }, [messages.length]);

  useEffect(() => {
    initChat();
    if (isOpen) getChatHistory();
  }, [isOpen]);

  useEffect(() => {
    if (!blockUserInput && messageInputRef.current)
      messageInputRef.current.focus();
  }, [blockUserInput]);

  const sendMessage = async (message: string) => {

    if (!checkEndpointURI()) return;

    const endpoint = `${import.meta.env.VITE_SUPPORT_CHAT_AI_URI}/learning_agent/admin/message`;

    const payload: ChatAgentRequestPayload = {
      session_id: sessionId,
      session_attributes: {
        corporate_id: "1"
      },
      payload: {
        end_session: false,
        user_message: message,
        user_id: "-1"
      }
    };

    setBlockUserInput(true);
    setIsSendingMessage(true);

    apiInstance
      .post(endpoint, payload)
      .then((res: AxiosResponse) => {
        console.log("res", res);
        setMessages([
          ...messages,
          {
            sender: "user",
            end_session: false,
            fist_message: true,
            response: message
          }
        ]);
      })
      .catch((error) => {
        console.error(
          "[ChatAgent] An error occurred sending message to agent",
          error
        );
        if (
          error.response &&
          error.response.data &&
          error.response.data.response
        ) {
          setApiError(error.response.data.response as string);
        } else if (error.message) {
          setApiError(`${error.message} [${error.code}]`);
        }
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  };

  const retrieveMessageFromAgent = async () => {

    if (!checkEndpointURI()) return;

    const endpoint = `${import.meta.env.VITE_SUPPORT_CHAT_AI_URI}/learning_agent/admin/response/stream?session_id=${sessionId}&corporate_id=1`;

    try {
      //Axios doesn't support SSE streaming, using fetch
      const response = await fetch(endpoint, {
        method: "GET",
        cache: "no-cache",
        headers: {
          Authorization: `Bearer ${sessionId}`,
          "x-ada-session-token": sessionId,
          "Content-Type": "application/json"
        }
      });

      if (!response.body) {
        console.error("[ChatAgent] Stream not available");
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";
      let done = false;

      while (!done) {
        const readResult = await reader.read();
        done = readResult.done;

        buffer += decoder.decode(readResult.value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.includes("event: error")) {
            const updatedMessages = [...messages];
            updatedMessages[updatedMessages.length - 1].end_session = true;
            setMessages(updatedMessages);
          } else if (line.startsWith("data:")) {
            const jsonString = line.replace(/^data:\s*/, "");
            try {
              const data: ChatAgentResponse = JSON.parse(jsonString);
              const updatedMessages = [...messages];
              updatedMessages[updatedMessages.length - 1].response += data.text;

              setMessages(updatedMessages);

              scrollToLastMessage();
            } catch (err) {
              console.error(
                "[ChatAgent] An error occurred parsing JSON SSE:",
                err
              );
            }
          }
        }
      }

      setBlockUserInput(false);
    } catch (error) {
      console.error(
        "[ChatAgent] An error has occurred while fetching agent's response:",
        error
      );
    }
  };

  const getChatHistory = () => {

    if (!checkEndpointURI()) return;

    setIsRetrievingHistory(true);
    const endpoint = `${import.meta.env.VITE_SUPPORT_CHAT_AI_URI}/learning_agent/admin/messages/${sessionId}?corporate_id=1`;

    apiInstance
      .get(endpoint)
      .then((res: AxiosResponse) => {
        if (res.data && res.data.messages && res.data.messages.length > 0) {
          setMessages(
            res.data.messages.map((message: { role: string, text: string }) => {
              return {
                sender: message.role,
                response: message.text,
                fist_message: false,
                end_session: false,
                from_history: true
              } as ChatAgentMessage;
            })
          );

          scrollToLastMessage();
        }
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status !== 404) {
          console.error(
            "[ChatAgent] An error occurred while retrieving agent chat history",
            error
          );
        }
      })
      .finally(() => {
        setIsRetrievingHistory(false);
      });
  };

  const initChat = () => {
    setApiError("");
    setBlockUserInput(false);
    setMessages([]);
  };

  const scrollToLastMessage = () => {
    if (messagesContainerRef && messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current!.scrollTo({
          top: messagesContainerRef.current!.scrollHeight,
          behavior: "auto"
        });
      }, 300);
    }
  };

  const checkEndpointURI = () => {
    if(!import.meta.env.VITE_SUPPORT_CHAT_AI_URI) {
      setApiError(t("ai_support_chat_endpoint_error"));
      console.error("No endpoint specified for the agent");
      return false;
    }

    return true;
  };

  const isSessionTerminated = messages.find((message) => message.end_session);

  return (
    <Modal
      open={ isOpen }
      onClose={ onClose }
      sx={ {
        zIndex: 1401
      } }
      slotProps={ {
        backdrop: {
          sx: {
            backgroundColor: "rgba(255, 255, 255, 0.05)"
          }
        }
      } }
    >
      <RootContainer>
        <ModalContainer theme={ theme }>
          <ModalHeaderContainer>
            <TitleContainer gap={ 2 }>
              { /*<AutoAwesome fontSize={ "large" } />*/ }
              <Typography variant="h4" component={ "h2" } fontWeight={ 700 } fontSize={ isMobile ? "1rem" : "1.5rem" }>
                { t("ai_support_chat_title") }
              </Typography>
            </TitleContainer>
            <ButtonCst
              id={ "close" }
              variant={ "contained" }
              size={ "medium" }
              sx={ {
                width: "32px",
                height: "32px",
                minWidth: "unset",
                padding: 1,
                zIndex: 1
              } }
              onClick={ onClose }
              disabled={ isSendingMessage }
            >
              <Icon
                icon={ "close" }
                size={ 19 }
              />
            </ButtonCst>
          </ModalHeaderContainer>

          <Divider theme={ theme } />

          <MessagesContainer
            theme={ theme }
            blockUserInput={ blockUserInput }
            ref={ messagesContainerRef }
            gap={ "20px" }
          >
            { messages && messages.length > 0 ? (
              <>
                { messages.map((message, index) => (
                  <AISupportChatMessageBox
                    key={ `chat_message_${index}` }
                    message={ message }
                    index={ index }
                  />
                )) }
              </>
            ) : (
              <WelcomeMessageContainer gap={ "20px" }>
                { /*<AutoAwesome sx={ { width: "60px", height: "60px" } } />*/ }
                <WelcomeMessage
                  dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(t("ai_support_chat_message") || "") } }
                />
                { isRetrievingHistory && (
                  <LoadingHistoryContainer>
                    <CircularProgress size={ 20 } />
                    <LoadingHistoryMessage>
                      { t("ai_support_chat_retrieving_history") }
                    </LoadingHistoryMessage>
                  </LoadingHistoryContainer>
                ) }
              </WelcomeMessageContainer>
            ) }
          </MessagesContainer>

          { apiError && (
            <AISupportChatErrorOverlay
              error={ apiError }
              onReset={ initChat }
            />
          ) }

          <Divider />

          { (isSessionTerminated || apiError) ? (
            <SessionTerminatedMessageContainer>
              <SessionTerminatedMessage>
                { t("ai_support_chat_session_terminated") }
              </SessionTerminatedMessage>
            </SessionTerminatedMessageContainer>
          ) : (
            <AISupportChatMessageInput
              isLoading={ isSendingMessage }
              onSendMessage={ (message) => sendMessage(message) }
              disabled={ blockUserInput || !!apiError }
              inputRef={ messageInputRef }
            />
          ) }
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default AISupportChat;
